// src/pages/CustomerLogin.jsx
import React, { useEffect, useState } from "react";
import { Smartphone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import loginImage from "../assets/login.jpeg";
import { adminApi } from "../utils/adminApi";
import "./CustomerLogin.css";

const CustomerLogin = () => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [otpExpiresIn, setOtpExpiresIn] = useState(0);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    sessionStorage.removeItem("bookingCustomer");
    sessionStorage.removeItem("bookingCustomerToken");
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setResendIn((value) => (value > 0 ? value - 1 : 0));
      setOtpExpiresIn((value) => (value > 0 ? value - 1 : 0));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const getCleanCustomer = () => {
    const cleanName = String(name).trim();
    const cleanPhone = String(phone).trim();
    const cleanEmail = String(email).trim().toLowerCase();

    if (!cleanName || !cleanPhone || !cleanEmail) {
      setError("Name, mobile and email are required.");
      return null;
    }

    if (!/^[6-9]\d{9}$/.test(cleanPhone)) {
      setError("Enter a valid 10-digit mobile number.");
      return null;
    }

    if (/^(\d)\1{9}$/.test(cleanPhone)) {
      setError("Invalid mobile number.");
      return null;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(cleanEmail)) {
      setError("Please enter a valid email address.");
      return null;
    }

    return {
      name: cleanName,
      phone: cleanPhone,
      email: cleanEmail
    };
  };

  const handleRequestOtp = async (event) => {
    event.preventDefault();
    if (isSendingOtp) return;

    setError("");
    setMessage("");

    const customer = getCleanCustomer();
    if (!customer) return;

    setIsSendingOtp(true);

    try {
      const response = await adminApi.post("/api/customer-auth/request-otp", customer);
      const resendInSeconds = Number(response.data?.resendInSeconds) || 60;
      const expiresInSeconds = Number(response.data?.expiresInSeconds) || 600;

      setOtpSent(true);
      setOtp("");
      setResendIn(resendInSeconds);
      setOtpExpiresIn(expiresInSeconds);
      setMessage("OTP sent to your email. Enter the 6-digit code to continue.");
    } catch (requestError) {
      const retryAfterSeconds = Number(requestError.response?.data?.retryAfterSeconds);
      if (retryAfterSeconds > 0) {
        setResendIn(retryAfterSeconds);
      }

      setError(
        requestError.response?.data?.error || "Unable to send OTP. Please try again."
      );
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    if (isVerifyingOtp) return;

    setError("");
    setMessage("");

    const customer = getCleanCustomer();
    if (!customer) return;

    const cleanOtp = String(otp).replace(/\D/g, "").slice(0, 6);

    if (!/^\d{6}$/.test(cleanOtp)) {
      setError("Enter a valid 6-digit OTP.");
      return;
    }

    setIsVerifyingOtp(true);

    try {
      const response = await adminApi.post("/api/customer-auth/verify-otp", {
        ...customer,
        otp: cleanOtp
      });

      const verifiedCustomer = response.data?.customer || customer;
      const customerToken = response.data?.token;

      if (!customerToken) {
        throw new Error("Missing customer token");
      }

      sessionStorage.setItem("bookingCustomer", JSON.stringify(verifiedCustomer));
      sessionStorage.setItem("bookingCustomerToken", customerToken);

      navigate("/brands", { replace: true });
    } catch (verifyError) {
      setError(
        verifyError.response?.data?.error || "OTP verification failed. Please try again."
      );
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const formatDuration = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  };

  const handleEditDetails = () => {
    setOtpSent(false);
    setOtp("");
    setResendIn(0);
    setOtpExpiresIn(0);
    setError("");
    setMessage("");
  };

  return (
    <div className="customer-login-page">
      <div className="customer-login-shell">
        <aside className="customer-login-side">
          <div className="customer-side-image-wrap">
            <img src={loginImage} alt="Phone repair service" className="customer-side-image" />
          </div>
         <h2>
  Repair Your
  <br />
  Phone with
  <br />
Servizy 
</h2>
          <p>Doorstep service | Genuine parts | Fast delivery</p>
        </aside>

        <section className="customer-login-card">
    

          <h1>Login / Signup</h1>
          <p className="customer-login-subtitle">
            {otpSent ? "Enter OTP sent to your email" : "Enter your details to continue"}
          </p>

          <form onSubmit={otpSent ? handleVerifyOtp : handleRequestOtp} className="customer-login-form">
            <input
              type="text"
              placeholder="Full Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              autoComplete="name"
              disabled={otpSent}
              required
            />

            <div className="customer-phone-input-wrap">
              <span className="customer-phone-icon" aria-hidden="true">
                <Smartphone size={16} />
              </span>
              <span className="customer-country-code">+91</span>
              <input
                type="tel"
                placeholder="Enter Mobile Number"
                value={phone}
                maxLength="10"
                pattern="[0-9]{10}"
                inputMode="numeric"
                autoComplete="tel-national"
                onChange={(event) => {
                  const digitsOnly = event.target.value.replace(/\D/g, "");
                  if (digitsOnly.length <= 10) {
                    setPhone(digitsOnly);
                  }
                }}
                disabled={otpSent}
                required
              />
            </div>
            <br/>
            

            <input
              type="email"
              placeholder="Email Address"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              disabled={otpSent}
              required
            />

            {otpSent ? (
              <input
                type="text"
                placeholder="Enter 6-digit OTP"
                value={otp}
                onChange={(event) =>
                  setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))
                }
                inputMode="numeric"
                pattern="[0-9]{6}"
                autoComplete="one-time-code"
                maxLength={6}
                required
              />
            ) : null}

            {otpSent ? (
              <p className="customer-login-helper" role="status" aria-live="polite">
                OTP expires in {formatDuration(otpExpiresIn)}
              </p>
            ) : null}

            {message ? (
              <p className="customer-login-message" role="status" aria-live="polite">
                {message}
              </p>
            ) : null}

            {error ? (
              <p className="customer-login-error" role="alert" aria-live="polite">
                {error}
              </p>
            ) : null}

            {otpSent ? (
              <>
                <button type="submit" disabled={isVerifyingOtp}>
                  {isVerifyingOtp ? "Verifying OTP..." : "Verify OTP & Continue"}
                </button>

                <div className="customer-login-actions">
                  <button
                    type="button"
                    className="customer-login-secondary-btn"
                    disabled={isSendingOtp || resendIn > 0}
                    onClick={handleRequestOtp}
                  >
                    {isSendingOtp
                      ? "Sending..."
                      : resendIn > 0
                        ? `Resend OTP in ${resendIn}s`
                        : "Resend OTP"}
                  </button>

                  <button
                    type="button"
                    className="customer-login-secondary-btn"
                    onClick={handleEditDetails}
                    disabled={isSendingOtp || isVerifyingOtp}
                  >
                    Edit Details
                  </button>
                </div>
              </>
            ) : (
              <button type="submit" disabled={isSendingOtp}>
                {isSendingOtp ? "Sending OTP..." : "Send OTP"}
              </button>
            )}
          </form>
        </section>
      </div>
    </div>
  );
};

export default CustomerLogin;
